import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { theme as antdTheme } from 'antd';

import { apexThemeMode } from '@totem/components/services/optimize/v2/dashboard/chartUtils';
import { MonthlyPerformance } from '@totem/components/services/optimize/v2/dashboard/types';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

type Props = {
  performanceMonths: MonthlyPerformance[];
  height?: number;
};

const SavingsStackedChart = ({ performanceMonths, height = 300 }: Props) => {
  const { useToken } = antdTheme;
  const { token } = useToken();

  // Split into actual vs projected; keep sorted order for the x-axis
  const actualMonths = performanceMonths.filter(
    (mon) => mon.entryType === 'actual',
  );
  const projectedMonths = performanceMonths.filter(
    (mon) => mon.entryType === 'projected',
  );
  const allMonths = [...actualMonths, ...projectedMonths];

  const categories = allMonths.map(
    (mon) => `${MONTH_NAMES[mon.month - 1]} ${mon.year}`,
  );

  // Actual series: real values for actual months, null for projected months
  const actualEnergy = allMonths.map((mon) =>
    mon.entryType === 'actual'
      ? parseFloat(mon.energyCostSavings.toFixed(2))
      : null,
  );
  const actualDemand = allMonths.map((mon) =>
    mon.entryType === 'actual'
      ? parseFloat(mon.demandCostSavings.toFixed(2))
      : null,
  );

  // Projected series: null for actual months, projected values for projected months
  const projectedEnergy = allMonths.map((mon) =>
    mon.entryType === 'projected'
      ? parseFloat(mon.energyCostSavings.toFixed(2))
      : null,
  );
  const projectedDemand = allMonths.map((mon) =>
    mon.entryType === 'projected'
      ? parseFloat(mon.demandCostSavings.toFixed(2))
      : null,
  );

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      background: 'transparent',
    },
    theme: { mode: apexThemeMode(token.colorBgBase) },
    plotOptions: {
      bar: {
        borderRadius: 2,
        columnWidth: '60%',
      },
    },
    fill: {
      // actual energy, actual demand solid; projected energy, projected demand faded
      opacity: [1, 1, 0.4, 0.4],
    },
    xaxis: {
      categories,
      labels: {
        rotate: -45,
        style: { fontSize: '11px' },
      },
    },
    yaxis: {
      title: { text: 'Cost Savings ($)' },
      labels: {
        formatter: (val: number) =>
          `$${val.toLocaleString('en-US', { maximumFractionDigits: 0 })}`,
      },
    },
    colors: ['#5B8FF9', '#F4A261', '#5B8FF9', '#F4A261'],
    legend: { position: 'top' },
    tooltip: {
      y: {
        formatter: (val: number) =>
          val !== null && val !== undefined
            ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : 'N/A',
      },
    },
    dataLabels: { enabled: false },
  };

  const series = [
    { name: 'Energy Savings', data: actualEnergy },
    { name: 'Demand Savings', data: actualDemand },
    { name: 'Projected Energy', data: projectedEnergy },
    { name: 'Projected Demand', data: projectedDemand },
  ];

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="bar"
      height={height}
    />
  );
};

export default SavingsStackedChart;
